import type { QuarantineRow, SiteDetail } from '@tabel/shared/schemas/sites';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { History, ShieldCheck } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { ErrorCodes, errorCode, useToast } from '../../../components/ErrorToast';
import { Button, Callout, Card, Modal, Skeleton, textareaCls } from '../../../components/ui';
import { ApiError, reportClientError } from '../../../lib/errors';
import { ruDateTime } from '../../../lib/format';
import { useRole } from '../../../lib/role';
import * as api from '../api';
import { sitesKeys } from '../api';
import { LoadError, SCREEN, shouldReport } from '../parts';
import { REPAIR, SITE } from '../texts';
import { RecordsUpload, RejectedRows } from './RecordsUpload';
import { useSite } from './actions';

/*
 * Раздел «Ремонт» страницы площадки (007 T047; spec.md FR-041, FR-043, US4; contracts/http-api.md, «Загрузки выгрузок
 * SmartPSS», «Тревоги и карантин»; data-model А.6, А.10). Подзаголовок `t-set-sub`, внутри — блоки по порядку:
 * - подсказка периода ремонта (`GET …/repair-hint`) — когда отметки могли не дойти;
 * - загрузка выгрузки записей: предпросмотр → «Добавить» (после запуска; до запуска — в шаге 10);
 * - история загрузок записей (`SiteDetail.uploads`);
 * - строки на проверке (`GET …/quarantine`) и «Выпустить» с причиной (`POST …/quarantine/release`).
 * Показывается только то, что прислал сервер; ФИО в разделе нет.
 */

/** Поле файла выгрузки в «Ремонте»: на него ведёт «Загрузить выгрузку» тревоги. */
export const REPAIR_FILE_ID = 'site-repair-file';
/** Заголовок строк на проверке: на него ведёт «Показать строки» тревоги. */
export const QUARANTINE_ID = 'site-quarantine';

/** «Ремонт» — у площадки, которую сервис наблюдает или считает: наблюдение, «Работает», «Нет связи». */
export function showsRepair(d: SiteDetail): boolean {
  return d.site.state === 'observing' || d.site.state === 'active' || d.site.state === 'no_link';
}

export function Repair() {
  const { detail } = useSite();
  // До запуска отметки за прошлые дни загружают в шаге 10: здесь — только строки на проверке.
  const launched = detail.site.launchedAt !== null;
  return (
    <section aria-labelledby="site-repair" className="mt-2">
      <h2 id="site-repair" className="t-set-sub">
        {REPAIR.title}
      </h2>
      {launched && (
        <>
          <p className="text-[13px] text-ink-2">{REPAIR.note}</p>
          <RepairHintBox />
          <Block id="site-repair-upload" title={REPAIR.uploadTitle}>
            <RecordsUpload inputId={REPAIR_FILE_ID} primary />
          </Block>
          <Block id="site-repair-history" title={REPAIR.historyTitle}>
            <UploadHistory />
          </Block>
        </>
      )}
      <Quarantine />
    </section>
  );
}

function Block({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="mt-5 flex flex-col gap-2">
      <h3 id={id} className="text-[14px] font-semibold text-ink">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** Сбой запроса раздела — в серверный журнал (принцип II); отказ по правам и данным — нет. */
function useReport(error: unknown, action: string) {
  useEffect(() => {
    if (error && shouldReport(error)) reportClientError(SCREEN, action, error);
  }, [error, action]);
}

/** Подсказка периода ремонта: перерывы приёма по журналу простоев и пропущенным выгрузкам (FR-041). */
function RepairHintBox() {
  const { detail } = useSite();
  const { role } = useRole();
  const code = detail.site.code;
  const tz = detail.site.tzOffset;
  const q = useQuery({ queryKey: sitesKeys.repairHint(role, code), queryFn: () => api.repairHint(code) });
  useReport(q.error, 'repair-hint');
  if (q.isError)
    return (
      <p className="mt-3 text-[12px] text-ink-3">
        {REPAIR.hintError}. <InlineCode error={q.error} />
      </p>
    );
  const periods = q.data?.periods ?? [];
  if (periods.length === 0) return null;
  return (
    <div data-testid="repair-hint" className="mt-3">
      <Callout tone="warn" icon={History}>
        <p>
          <b className="font-semibold">{REPAIR.hintTitle}</b>
        </p>
        <ul className="mt-1 flex flex-col gap-0.5 tabular-nums">
          {periods.map((p) => (
            <li key={`${p.from}-${p.to}-${p.reason}`}>
              {REPAIR.period(ruDateTime(p.from, tz), ruDateTime(p.to, tz))}
              {/* Причина — кодом (`RepairReason`), слова — `REPAIR.reasons`. */}
              <span className="text-ink-2"> · {REPAIR.reasons[p.reason]}</span>
            </li>
          ))}
        </ul>
        <p className="mt-1">{REPAIR.hintText}</p>
      </Callout>
    </div>
  );
}

/** Код ошибки (requestId) строкой — для мелких ошибок внутри раздела. */
function InlineCode({ error }: { error: unknown }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  if (!requestId && !code) return null;
  return (
    <>
      {SITE.errorCode} <ErrorCodes requestId={requestId} code={code} className="text-ink-2" />
    </>
  );
}

/** История загрузок записей: когда, какой файл, итог. Новые сверху. */
function UploadHistory() {
  const { detail } = useSite();
  const tz = detail.site.tzOffset;
  const list = detail.uploads
    .filter((u) => u.kind === 'records')
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  if (list.length === 0) return <p className="text-[13px] text-ink-2">{REPAIR.historyEmpty}</p>;
  return (
    <ul className="rounded-control border border-line">
      {list.map((u) => (
        <li key={u.id} className="border-b border-line px-3 py-2.5 text-[13px] last:border-b-0">
          <div className="flex flex-wrap gap-x-2 text-ink">
            <span className="tabular-nums">{ruDateTime(u.at, tz)}</span>
            <span className="min-w-0 break-all">{u.fileName}</span>
          </div>
          <div className="text-[12px] text-ink-2 tabular-nums">
            {REPAIR.result.text(u.added, u.duplicates, u.rejected)}
          </div>
          <RejectedRows rows={u.rejectedRows} counts={u.rejectedByReason} />
        </li>
      ))}
    </ul>
  );
}

// ── Строки на проверке (карантин) ──

const REASON_MIN = 5;
const REASON_MAX = 500;

function Quarantine() {
  const { detail } = useSite();
  const { role } = useRole();
  const code = detail.site.code;
  const tz = detail.site.tzOffset;
  const Q = REPAIR.quarantine;
  const q = useQuery({ queryKey: sitesKeys.quarantine(role, code), queryFn: () => api.quarantine(code) });
  useReport(q.error, 'quarantine');
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [asking, setAsking] = useState<{ ids: number[]; retry: boolean } | null>(null);
  // Список строк не опрашивается; тревога «Отметки ждут проверки» приходит с опросом страницы. Число строк в ней
  // изменилось — список перечитывается.
  const qc = useQueryClient();
  const alertRows = detail.alerts.find((a) => a.kind === 'quarantine')?.details.rows;
  const alerted = typeof alertRows === 'number' ? alertRows : 0;
  const seen = useRef(alerted);
  useEffect(() => {
    if (seen.current === alerted) return;
    seen.current = alerted;
    void qc.invalidateQueries({ queryKey: sitesKeys.quarantine(role, code) });
  }, [alerted, qc, role, code]);

  const rows = q.data ?? [];
  const waiting = rows.filter((r) => r.releasedAt === null);
  // Выбор — только среди строк, которые ещё ждут: выпущенные после перечитывания уходят из выбора.
  const ids = waiting.filter((r) => selected.has(r.sourceChangeId)).map((r) => r.sourceChangeId);
  const all = waiting.length > 0 && ids.length === waiting.length;
  const toggle = (id: number) =>
    setSelected((s) => {
      const next = new Set(s);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  let content: ReactNode;
  if (q.isError)
    content = (
      <LoadError title={Q.loadError} error={q.error} busy={q.isFetching} onRetry={() => void q.refetch()} />
    );
  else if (!q.data)
    content = (
      <Card className="space-y-3 p-4" aria-busy="true" aria-label={SITE.loading}>
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-5 w-1/3" />
      </Card>
    );
  else if (rows.length === 0) content = <p className="text-[13px] text-ink-2">{Q.empty}</p>;
  else
    content = (
      <>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="inline-flex min-h-9 cursor-pointer items-center gap-2 text-[13px] text-ink max-sm:min-h-10">
            <input
              type="checkbox"
              className="t-check"
              checked={all}
              disabled={waiting.length === 0}
              onChange={() => setSelected(all ? new Set() : new Set(waiting.map((r) => r.sourceChangeId)))}
            />
            {Q.selectAll}
          </label>
          <Button disabled={ids.length === 0} onClick={() => setAsking({ ids: [...ids], retry: false })}>
            {ids.length > 0 ? Q.release(ids.length) : Q.submit}
          </Button>
        </div>
        <QuarantineTable
          rows={rows}
          tz={tz}
          selected={selected}
          onToggle={toggle}
          onRetry={(id) => setAsking({ ids: [id], retry: true })}
        />
      </>
    );

  return (
    <section aria-labelledby={QUARANTINE_ID} className="mt-5 flex flex-col gap-2">
      <h3 id={QUARANTINE_ID} tabIndex={-1} className="text-[14px] font-semibold text-ink focus:outline-none">
        {Q.title}
      </h3>
      <p className="text-[13px] text-ink-2">{Q.note}</p>
      {content}
      <ReleaseDialog
        ids={asking?.ids ?? null}
        retry={asking?.retry ?? false}
        onClose={() => setAsking(null)}
        onDone={() => {
          setAsking(null);
          setSelected(new Set());
        }}
      />
    </section>
  );
}

/**
 * Таблица строк на проверке (дизайн-система DataTable, `t-tbl-stack`): номер строки копии закреплён, на телефоне строки —
 * карточки, выбор строки — в конце карточки (цель нажатия 40 px). Выпущенную строку выбрать нельзя.
 */
function QuarantineTable({
  rows,
  tz,
  selected,
  onToggle,
  onRetry,
}: {
  rows: readonly QuarantineRow[];
  tz: string;
  selected: ReadonlySet<number>;
  onToggle: (id: number) => void;
  onRetry: (id: number) => void;
}) {
  const Q = REPAIR.quarantine;
  return (
    <Card className="overflow-x-auto">
      <table className="t-tbl t-tbl-stack" aria-label={Q.title}>
        <thead>
          <tr>
            <th className="w-10">
              <span className="sr-only">{Q.selectAll}</span>
            </th>
            <th className="sticky">{Q.columns.row}</th>
            <th>{Q.columns.reason}</th>
            <th>{Q.columns.seen}</th>
            <th>{Q.columns.state}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.sourceChangeId} className={selected.has(r.sourceChangeId) ? 'sel' : ''}>
              <td className="w-10 max-sm:order-last">
                {r.releasedAt === null && (
                  <label className="inline-flex min-h-8 min-w-8 cursor-pointer items-center justify-center max-sm:min-h-10 max-sm:min-w-10">
                    <input
                      type="checkbox"
                      className="t-check"
                      aria-label={Q.select(r.sourceChangeId)}
                      checked={selected.has(r.sourceChangeId)}
                      onChange={() => onToggle(r.sourceChangeId)}
                    />
                  </label>
                )}
              </td>
              <td className="sticky tabular-nums">
                <span className="sm:hidden">{Q.columns.row} </span>
                {r.sourceChangeId}
              </td>
              <td className="min-w-[220px] whitespace-normal max-sm:min-w-0">{Q.reasons[r.reason]}</td>
              <td className="tabular-nums">{ruDateTime(r.seenAt, tz)}</td>
              <td>
                {r.releasedAt === null ? (
                  Q.waiting
                ) : (
                  <>
                    <div>{Q.released(ruDateTime(r.releasedAt, tz))}</div>
                    <Button
                      variant="ghost"
                      aria-label={Q.retryRow(r.sourceChangeId)}
                      onClick={() => onRetry(r.sourceChangeId)}
                    >
                      {Q.retry}
                    </Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/** «Выпустить N строк в обработку?»: причина 5–500 знаков, она попадёт в журнал действий. */
function ReleaseDialog({
  ids,
  retry,
  onClose,
  onDone,
}: { ids: readonly number[] | null; retry: boolean; onClose: () => void; onDone: () => void }) {
  const id = useId();
  const { detail } = useSite();
  const { role } = useRole();
  const code = detail.site.code;
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const Q = REPAIR.quarantine;
  const [reason, setReason] = useState('');
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const open = ids !== null && ids.length > 0;
  useEffect(() => {
    if (open) return;
    setReason('');
    setError(false);
    setBusy(false);
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ids || busy) return;
    const text = reason.trim();
    if (text.length < REASON_MIN || text.length > REASON_MAX) {
      setError(true);
      return;
    }
    setBusy(true);
    try {
      // Сколько строк выпущено — число сервера (`QuarantineReleased`).
      const { released, imported } = await api.releaseQuarantine(code, { ids: [...ids], reason: text });
      showInfo(Q.done(released, imported));
      onDone();
      void qc.invalidateQueries({ queryKey: sitesKeys.quarantine(role, code) });
      // Тревога «Отметки ждут проверки» закроется, когда строк не останется: страница площадки перечитывается.
      void qc.invalidateQueries({ queryKey: sitesKeys.site(role, code) });
    } catch (err) {
      setBusy(false);
      showError(err, { screen: SCREEN, action: 'quarantine-release' });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={open ? (retry ? Q.retryTitle(ids.length) : Q.confirmTitle(ids.length)) : ''}
      description={retry ? REPAIR.preview.note : Q.confirmText}
    >
      <form noValidate onSubmit={(e) => void submit(e)}>
        <label htmlFor={`${id}-reason`} className="mb-1.5 block text-[13px] font-medium text-ink">
          {Q.reasonLabel}
        </label>
        <textarea
          id={`${id}-reason`}
          className={textareaCls}
          rows={3}
          maxLength={REASON_MAX}
          value={reason}
          aria-invalid={error || undefined}
          aria-describedby={`${id}-hint`}
          data-autofocus
          onChange={(e) => {
            setReason(e.target.value);
            setError(false);
          }}
        />
        <span id={`${id}-hint`} className={`mt-1 block text-[12px] ${error ? 'text-bad' : 'text-ink-3'}`}>
          {Q.reasonHint}
        </span>
        <div className="mt-6 flex flex-wrap justify-end gap-2 max-sm:[&>button]:flex-auto">
          <Button onClick={onClose}>{REPAIR.preview.cancel}</Button>
          <Button type="submit" variant="primary" icon={ShieldCheck} disabled={busy}>
            {retry ? Q.retry : Q.submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
